import { NotFoundHeader, NotFoundText, ServiceUnavailableHeader, ServiceUnavailableText } from "constants/text.ts";
import {
	AcknowledgementConfigCardTitleText,
	BillToAddEmailButton,
	BillToCancelEmailButton,
	BillToDefaultSettingsText,
	BillToEmailFieldTitle,
	BillToEmailFieldUniqueErrorText,
	BillToNoDataErrorButton,
	BillToRemoveEmailButton,
	BillToSaveEmailButton,
	BillToSaveLoadingText,
	BillToSettingsCartHeaderText,
	BillToSettingsCautionHeaderText,
	BillToSettingsCautionLine1Text,
	BillToSettingsCautionLine2Text,
	BillToSettingsHeaderText,
	BillToSettingsMaxCards,
	BillToSettingsSaveFailedText,
	BillToSettingsSaveSuccessText,
	MaxCardsErrorText,
	ReceiveAcknowledgementEmailsOptionText,
	ReceiveAcknowledgementEmailsText
} from "components/Preferences/text.ts";
import {
	AcknowledgementCheckbox,
	AcknowledgementCheckboxText,
	AcknowledgementConfigCard,
	AcknowledgementConfigCardHeader,
	BillToSettingsButton,
	BillToSettingsButtonGroup,
	BillToSettingsButtonRow,
	BillToSettingsCardHeader,
	BillToSettingsContainer,
	BillToSettingsErrorBoxWrapper,
	BillToSettingsPageHeader,
	BottomStickyRow,
	DefaultSettingsAccordion,
	DeleteButton,
	EmailAddressFieldWrapper,
	Header,
	SkeletonButtonRow,
	WarningBoxContainer
} from "./BillToSettingsPage.styles.ts";
import { useNavigate, useParams } from "react-router";
import { AccordionDetails, AccordionSummary, Alert, Checkbox, Skeleton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	AcknowledgementPriceFormat,
	AcknowledgementPriceFormatType,
	AcknowledgmentEmailFormat,
	AcknowledgmentEmailFormatType,
	UserPermission
} from "types/declarations/enums.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AckEmailFormValues, FormSchema, formSchema } from "./utils/schema.ts";
import WarningBox from "components/Common/WarningBox/WarningBox.tsx";
import { useGetBillToInfoQuery } from "features/api/accountApi.ts";
import { APIErrorFaceBox } from "components/Common/APIErrorFaceBox/APIErrorFaceBox.tsx";
import { useUpdateBillToEmailRecipientsMutation } from "features/api/permissionsApi.ts";
import BillToSettingsEmailRecipientsConfig from "components/Preferences/BillToSettings/BillToSettingsEmailRecipientsConfig.tsx";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import APIErrorCustomBanner from "components/Common/APIErrorCustomBanner/APIErrorCustomBanner.tsx";

const BillToSettingsPage = () => {
	const { billToId } = useParams();
	const { accountId } = useParams();
	const navigate = useNavigate();
	const billToInfoQueryArgs = useMemo(() => ({ accountId: accountId, billToId: billToId }), [accountId, billToId]);
	const {
		data: billTo,
		error: billToInfoError,
		isLoading: isBillToInfoLoading
	} = useGetBillToInfoQuery(billToInfoQueryArgs);
	const [updateBillToEmailRecipients, { isError: isUpdateError }] = useUpdateBillToEmailRecipientsMutation();
	const { data: userDetails } = useGetUserDetailsQuery();
	const isAdmin = userDetails?.permissions?.find((x) => x === UserPermission.ADMIN) ?? false;
	const containerRef = useRef<HTMLFormElement | null>(null);
	const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
	const { configurationsData } = useContext(ConfigurationsContext);
	const isUserPreferenceEnabled = configurationsData?.featureFlags?.userPreferenceEnabled ?? false;

	const [attemptedAddOrSave, setAttemptedAddOrSave] = useState(false);
	const [defaultAckEmailFormat, setDefaultAckEmailFormat] = useState<AcknowledgmentEmailFormatType>(
		billTo?.acknowledgements?.defaultEmailFormat ?? AcknowledgmentEmailFormat.EXPANDED
	);
	const [defaultAckPriceFormat, setDefaultAckPriceFormat] = useState<AcknowledgementPriceFormatType>(
		billTo?.acknowledgements?.defaultPriceFormat ?? AcknowledgementPriceFormat.LIST
	);
	const [cardLimitError, setCardLimitError] = useState<string | null>(null);
	const [saveMessage, setSaveMessage] = useState<string | undefined>(undefined);
	const [isSaving, setIsSaving] = useState(false);
	const { snackbarOpen } = useSnackbarEffect(isSaving, saveMessage);

	const initialCards: FormSchema[] = useMemo(
		() =>
			billTo?.acknowledgements?.recipients?.map((rec) => ({
				email: rec.email ?? "",
				isAckReceive: rec.isEnabled ?? true,
				ackEmailFormat:
					rec.emailFormat ??
					billTo?.acknowledgements?.defaultEmailFormat ??
					AcknowledgmentEmailFormat.EXPANDED,
				ackPriceFormat:
					rec.priceFormat ?? billTo?.acknowledgements?.defaultPriceFormat ?? AcknowledgementPriceFormat.LIST
			})) ?? [],
		[billTo]
	);

	const {
		control,
		handleSubmit,
		trigger,
		formState: { errors, touchedFields, isSubmitted },
		getValues,
		register,
		setError,
		clearErrors,
		watch,
		getFieldState,
		reset
	} = useForm<AckEmailFormValues>({
		mode: "onChange",
		resolver: yupResolver(formSchema),
		reValidateMode: "onChange",
		defaultValues: {
			cards: initialCards
		}
	});
	const { fields, append, remove } = useFieldArray({
		control,
		name: "cards"
	});

	useEffect(() => {
		if (!billTo) return;

		const acknowledgements = billTo.acknowledgements ?? {};
		const recipients = acknowledgements.recipients ?? [];

		const mappedCards: FormSchema[] = recipients.map((rec) => ({
			email: rec.email ?? "",
			isAckReceive: rec.isEnabled ?? true,
			ackEmailFormat: rec.emailFormat ?? AcknowledgmentEmailFormat.EXPANDED,
			ackPriceFormat: rec.priceFormat ?? AcknowledgementPriceFormat.LIST
		}));

		reset({ cards: mappedCards });
		setDefaultAckEmailFormat(acknowledgements.defaultEmailFormat ?? AcknowledgmentEmailFormat.EXPANDED);
		setDefaultAckPriceFormat(acknowledgements.defaultPriceFormat ?? AcknowledgementPriceFormat.LIST);
	}, [billTo, reset, setDefaultAckEmailFormat, setDefaultAckPriceFormat]);

	const checkBoxLabel = () => {
		return (
			<AcknowledgementCheckboxText>
				<div>{ReceiveAcknowledgementEmailsOptionText}</div>
				<div>{ReceiveAcknowledgementEmailsText}</div>
			</AcknowledgementCheckboxText>
		);
	};

	const createEmptyCard = (): FormSchema => ({
		email: "",
		isAckReceive: true,
		ackEmailFormat: defaultAckEmailFormat,
		ackPriceFormat: defaultAckPriceFormat
	});

	const validateUniqueEmails = (cards: FormSchema[]): boolean => {
		const firstIndexByEmail = new Map<string, number>();
		let allUnique = true;

		cards.forEach((card, index) => {
			const raw = card.email ?? "";
			const email = raw.trim().toLowerCase();

			if (!email) {
				return;
			}

			const firstIndex = firstIndexByEmail.get(email);
			if (firstIndex === undefined) {
				firstIndexByEmail.set(email, index);

				const fieldState = getFieldState(`cards.${index}.email`);
				if (fieldState.error?.message === BillToEmailFieldUniqueErrorText) {
					clearErrors(`cards.${index}.email`);
				}
			} else {
				allUnique = false;

				setError(`cards.${index}.email`, {
					type: "manual",
					message: BillToEmailFieldUniqueErrorText
				});
			}
		});

		return allUnique;
	};
	const handleAddCard = async () => {
		setAttemptedAddOrSave(true);
		const cards = getValues("cards") ?? [];
		const valid = await trigger("cards");

		const unique = validateUniqueEmails(cards);
		if (!valid || !unique) {
			return;
		}

		if (cards?.length >= BillToSettingsMaxCards) {
			setCardLimitError(MaxCardsErrorText);
			return;
		}
		append(createEmptyCard());
		setTimeout(() => {
			const lastCard = cardRefs.current[fields.length];
			lastCard?.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 100);
	};

	const handleRemoveCard = (index: number) => {
		remove(index);
		setCardLimitError(null);
	};

	if (billToInfoError) {
		const statusInCustomQuoteDataError = "status" in billToInfoError;
		const is404Error = statusInCustomQuoteDataError && billToInfoError.status === 404;
		return (
			<BillToSettingsErrorBoxWrapper>
				<APIErrorFaceBox
					buttonText={BillToNoDataErrorButton}
					buttonClick={() => navigate("/admin-settings")}
					headerText={is404Error ? NotFoundHeader : ServiceUnavailableHeader}
					descriptionText={is404Error ? NotFoundText : ServiceUnavailableText}
					errorCode={statusInCustomQuoteDataError ? billToInfoError.status : null}
					faceBoxType={is404Error ? "notFound" : "generic"}
				/>
			</BillToSettingsErrorBoxWrapper>
		);
	}

	const onSubmit = async (values: AckEmailFormValues) => {
		try {
			setAttemptedAddOrSave(true);
			setSaveMessage(undefined);
			const valid = await trigger("cards");
			const unique = validateUniqueEmails(values.cards);

			if (!valid || !unique) {
				return;
			}
			setIsSaving(true);

			const payload = {
				accountId: accountId,
				billToId: billToId,
				acknowledgements: {
					defaultEmailFormat: defaultAckEmailFormat,
					defaultPriceFormat: defaultAckPriceFormat,
					recipients: values.cards.map((card) => ({
						email: card.email,
						isEnabled: card.isAckReceive,
						emailFormat: card.ackEmailFormat,
						priceFormat: card.ackPriceFormat
					}))
				}
			};
			await updateBillToEmailRecipients(payload).unwrap();
			setSaveMessage(BillToSettingsSaveSuccessText);
			setTimeout(() => {
				window.history.back();
			}, 1000);
		} catch (e) {
			setSaveMessage(BillToSettingsSaveFailedText);
		} finally {
			setIsSaving(false);
		}
	};

	const onCancel = () => {
		window.history.back();
	};

	if (!isAdmin || !isUserPreferenceEnabled) {
		return (
			<div>
				<APIErrorCustomBanner statusCode={403} />
			</div>
		);
	}
	return (
		<>
			<BillToSettingsPageHeader>
				<h1>
					{BillToSettingsHeaderText} {[billToId, billTo?.projectName].filter(Boolean).join(" - ")}
				</h1>
			</BillToSettingsPageHeader>
			<BillToSettingsContainer>
				<BillToSettingsCardHeader>{BillToSettingsCartHeaderText}</BillToSettingsCardHeader>
				<WarningBoxContainer data-testid="billto-settings-info-box">
					<WarningBox
						isInfo
						warning={BillToSettingsCautionHeaderText}
						message={
							<div style={{ padding: 0 }}>
								<span>{BillToSettingsCautionLine1Text}</span>
								<span>{BillToSettingsCautionLine2Text}</span>
							</div>
						}
					/>
				</WarningBoxContainer>
				{isBillToInfoLoading ? (
					<Skeleton
						variant="text"
						width="100%"
						height="100px"
					/>
				) : (
					<DefaultSettingsAccordion data-testid="default-settings-accordion">
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>{BillToDefaultSettingsText}</AccordionSummary>
						<AccordionDetails>
							<BillToSettingsEmailRecipientsConfig
								layoutField={{
									value: defaultAckEmailFormat,
									onChange: (v) => setDefaultAckEmailFormat(v)
								}}
								formatField={{
									value: defaultAckPriceFormat,
									onChange: (v) => setDefaultAckPriceFormat(v)
								}}
							/>
						</AccordionDetails>
					</DefaultSettingsAccordion>
				)}
				<form
					ref={containerRef}
					id="ack-config-form"
					onSubmit={handleSubmit(onSubmit)}
				>
					{fields.map((field, index) => {
						const emailError = errors.cards?.[index]?.email?.message;
						const isAckReceive = watch(`cards.${index}.isAckReceive`);
						const emailFieldTouched = touchedFields.cards?.[index]?.email;
						const showError = !!emailError && (emailFieldTouched || isSubmitted || attemptedAddOrSave);

						return (
							<AcknowledgementConfigCard
								ref={(el: HTMLDivElement | null) => {
									cardRefs.current[index] = el;
								}}
								key={index}
							>
								<AcknowledgementConfigCardHeader>
									<Header>
										{AcknowledgementConfigCardTitleText}
										{index + 1}
									</Header>
								</AcknowledgementConfigCardHeader>
								<EmailAddressFieldWrapper>
									<TextField
										data-testid={`ack-email-field-${index}`}
										label={BillToEmailFieldTitle}
										title={BillToEmailFieldTitle}
										size="small"
										fullWidth
										InputLabelProps={{ shrink: true }}
										{...register(`cards.${index}.email` as const)}
										error={showError}
										helperText={showError ? emailError : ""}
									/>
								</EmailAddressFieldWrapper>

								<div style={{ margin: "16px" }}>
									<Controller
										control={control}
										name={`cards.${index}.isAckReceive`}
										render={({ field: checkBoxField }) => (
											<AcknowledgementCheckbox
												control={
													<Checkbox
														size="medium"
														checked={!!checkBoxField.value}
														onChange={(c) => checkBoxField.onChange(c.target.checked)}
													/>
												}
												label={checkBoxLabel()}
												labelPlacement="end"
											/>
										)}
									/>
									{isAckReceive && (
										<Controller
											control={control}
											name={`cards.${index}.ackEmailFormat`}
											render={({ field: layoutField }) => (
												<Controller
													control={control}
													name={`cards.${index}.ackPriceFormat`}
													render={({ field: formatField }) => (
														<BillToSettingsEmailRecipientsConfig
															layoutField={{
																value: layoutField.value,
																onChange: layoutField.onChange
															}}
															formatField={{
																value: formatField.value,
																onChange: formatField.onChange
															}}
														/>
													)}
												/>
											)}
										/>
									)}
								</div>
								<DeleteButton
									onClick={() => handleRemoveCard(index)}
									variant="text"
								>
									<DeleteIcon />
									<span>{BillToRemoveEmailButton}</span>
								</DeleteButton>
							</AcknowledgementConfigCard>
						);
					})}
				</form>
			</BillToSettingsContainer>

			<BottomStickyRow data-testid="billto-settings-bottom-row">
				{isBillToInfoLoading || isSaving ? (
					<SkeletonButtonRow>
						<Skeleton
							variant="text"
							width="100%"
							height={70}
						/>
						<Skeleton
							variant="text"
							width="100%"
							height={70}
						/>
						<Skeleton
							variant="text"
							width="100%"
							height={70}
						/>
						<Skeleton
							variant="text"
							width="100%"
							height={70}
						/>
						<Skeleton
							variant="text"
							width="100%"
							height={70}
						/>
					</SkeletonButtonRow>
				) : (
					<>
						<div>
							{cardLimitError && (
								<Alert
									severity="error"
									sx={{ mt: 2 }}
								>
									{cardLimitError}
								</Alert>
							)}
						</div>
						<BillToSettingsButtonRow>
							<BillToSettingsButton
								variant="outlined"
								color="primary"
								size="large"
								onClick={handleAddCard}
							>
								{BillToAddEmailButton}
							</BillToSettingsButton>
							<BillToSettingsButtonGroup>
								<BillToSettingsButton
									variant="outlined"
									color="primary"
									size="large"
									onClick={onCancel}
								>
									{BillToCancelEmailButton}
								</BillToSettingsButton>
								<BillToSettingsButton
									variant="contained"
									color="primary"
									size="large"
									type="submit"
									form="ack-config-form"
								>
									{BillToSaveEmailButton}
								</BillToSettingsButton>
							</BillToSettingsButtonGroup>
						</BillToSettingsButtonRow>
					</>
				)}
			</BottomStickyRow>
			<Snackbar
				open={snackbarOpen}
				isLoading={isSaving}
				loadingText={BillToSaveLoadingText}
				successText={saveMessage ?? ""}
				isError={isUpdateError}
				errorText={saveMessage ?? ""}
			/>
		</>
	);
};

export default BillToSettingsPage;
